import { com } from '@eidu/form'
import { useCallback, useContext, useMemo } from 'react'
import { AuthContext, IAuthContext } from 'react-oauth2-code-pkce'
import { useInfiniteQuery } from '@tanstack/react-query'
import EqualityHashMap from '../util/EqualityHashMap'
import EntityWithLabelAndRelated from '../domain/entity/EntityWithLabelAndRelated'
import { createAuthContext } from '../api/authorization/AuthenticationContext'
import { requireNotUndefinedOrNull } from '../util/require'
import getEntitiesOfType from '../api/entity/data/getEntitiesOfType'
import { getRelated } from '../domain/entity/getEntitiesFromResponses'
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntityId = com.eidu.sharedlib.entity.EntityId
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import Entity = com.eidu.sharedlib.entity.Entity
import KeysetPaginatedResponseWithRelated = com.eidu.api.pagination.KeysetPaginatedResponseWithRelated
import PaginatedResponseWithRelated = com.eidu.api.pagination.PaginatedResponseWithRelated

export type UsePaginatedEntitiesResult = {
  entities: EntityWithLabelAndRelated[]
  loadMore: () => void
  reload: () => void
  hasMore: boolean
  loading: boolean
}

type UsePaginatedEntitiesParams = {
  typeId: EntityTypeId
  searchQuery?: string
  allTypes: ReadonlyMap<EntityTypeId, EntityType | null>
  pageSize?: number
}

const DEFAULT_PAGE_SIZE = 50

const getEntitiesFromResponse = (
  response: PaginatedResponseWithRelated<Entity>,
  types: ReadonlyMap<EntityTypeId, EntityType | null>
): [EntityId, EntityWithLabelAndRelated][] => {
  const relatedEntitiesById =
    response.relatedDocuments
      ?.asJsReadonlyArrayView()
      ?.let((related) => new EqualityHashMap(Array.from(related).map((it) => [it.id, it]))) ??
    new Map<EntityId, Entity>()

  return response.documents.asJsReadonlyArrayView().map((entity) => getRelated(entity, types, relatedEntitiesById))
}

const usePaginatedEntitiesOfType = ({
  typeId,
  searchQuery,
  allTypes,
  pageSize = DEFAULT_PAGE_SIZE,
}: UsePaginatedEntitiesParams): UsePaginatedEntitiesResult => {
  const iAuthContext = useContext<IAuthContext>(AuthContext)
  const authContext = useMemo(
    () => createAuthContext(iAuthContext),
    [iAuthContext.token, iAuthContext.idTokenData?.organizationId]
  )

  const query = useInfiniteQuery({
    queryKey: ['entities', typeId.toString(), searchQuery ?? ''],
    queryFn: async ({ pageParam, signal }: { pageParam: string | undefined; signal: AbortSignal }) => {
      const response = await getEntitiesOfType({
        id: typeId,
        pageSize,
        cursor: pageParam || '',
        query: searchQuery ?? '',
        withRelatedEntities: true,
        authContext: requireNotUndefinedOrNull(authContext),
        signal,
      })

      if (response instanceof KeysetPaginatedResponseWithRelated) {
        const entities = getEntitiesFromResponse(response, allTypes).map(([_, entity]) => entity)
        return {
          entities,
          nextCursor: response.next || undefined,
        }
      } else {
        throw new Error('Expected cursor-based pagination response')
      }
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: undefined,
    enabled: !!authContext,
  })

  const entities = useMemo(
    () =>
      query.data?.pages.flatMap(
        (page: { entities: EntityWithLabelAndRelated[]; nextCursor: string | undefined }) => page.entities
      ) ?? [],
    [query.data?.pages.length, query.dataUpdatedAt]
  )

  const loading = query.isLoading || query.isFetchingNextPage || query.isRefetching
  const hasMore = query.hasNextPage ?? false

  const loadMore = useCallback(() => {
    if (query.hasNextPage && !query.isFetchingNextPage) {
      query.fetchNextPage()
    }
  }, [query])

  const reload = useCallback(() => {
    query.refetch()
  }, [query])

  return { entities, loadMore, reload, hasMore, loading }
}

export default usePaginatedEntitiesOfType
